import { useEffect, useRef, useCallback } from 'react';

interface PerformanceConfig {
  maxErrorsPerMinute: number;
  maxHealthChecksPerMinute: number;
  errorThrottleWindow: number;
  debugMode: boolean;
}

// Configuración por defecto optimizada
const DEFAULT_CONFIG: PerformanceConfig = {
  maxErrorsPerMinute: 5,
  maxHealthChecksPerMinute: 2,
  errorThrottleWindow: 60000, // 1 minuto
  debugMode: false
};

// Singleton para gestión global de rendimiento
class PerformanceManager {
  private static instance: PerformanceManager;
  private errorCounts: Map<string, number[]> = new Map();
  private healthCheckTimes: number[] = [];
  private config: PerformanceConfig;

  constructor(config: PerformanceConfig = DEFAULT_CONFIG) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    
    // Limpiar contadores cada minuto
    setInterval(() => {
      this.cleanupOldEntries();
    }, 60000);
  }

  static getInstance(config?: PerformanceConfig): PerformanceManager {
    if (!PerformanceManager.instance) {
      PerformanceManager.instance = new PerformanceManager(config);
    }
    return PerformanceManager.instance;
  }

  private cleanupOldEntries() {
    const now = Date.now();
    const cutoff = now - this.config.errorThrottleWindow;

    // Limpiar contadores de errores
    this.errorCounts.forEach((times, key) => {
      const filtered = times.filter(time => time > cutoff);
      if (filtered.length === 0) {
        this.errorCounts.delete(key);
      } else {
        this.errorCounts.set(key, filtered);
      }
    });

    // Limpiar health checks
    this.healthCheckTimes = this.healthCheckTimes.filter(time => time > cutoff);
  }

  shouldLogError(errorKey: string): boolean {
    const now = Date.now();
    const errorTimes = this.errorCounts.get(errorKey) || [];
    
    // Filtrar errores antiguos
    const recentErrors = errorTimes.filter(time => 
      now - time < this.config.errorThrottleWindow
    );

    if (recentErrors.length >= this.config.maxErrorsPerMinute) {
      if (this.config.debugMode) {
        console.warn(`🚫 Error throttled: ${errorKey} (${recentErrors.length}/${this.config.maxErrorsPerMinute})`);
      }
      return false;
    }

    // Registrar este error
    recentErrors.push(now);
    this.errorCounts.set(errorKey, recentErrors);
    
    return true;
  }

  shouldPerformHealthCheck(): boolean {
    const now = Date.now();
    const cutoff = now - 60000; // Última hora
    const recentChecks = this.healthCheckTimes.filter(time => time > cutoff);
    
    if (recentChecks.length >= this.config.maxHealthChecksPerMinute) {
      if (this.config.debugMode) {
        console.warn(`🚫 Health check throttled (${recentChecks.length}/${this.config.maxHealthChecksPerMinute})`);
      }
      return false;
    }

    this.healthCheckTimes.push(now);
    return true;
  }

  // Throttled logging function
  throttledLog(level: 'log' | 'warn' | 'error', message: string, data?: any) {
    const errorKey = `${level}:${message.substring(0, 50)}`;
    
    if (this.shouldLogError(errorKey)) {
      console[level](message, data);
    }
  }

  // Optimized fetch with automatic retry and timeout
  async optimizedFetch(
    url: string, 
    options: RequestInit = {}, 
    retries: number = 2
  ): Promise<Response> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 segundos timeout

    const fetchOptions = {
      ...options,
      signal: controller.signal
    };

    try {
      const response = await fetch(url, fetchOptions);
      clearTimeout(timeoutId);
      
      // Solo reintentar en errores específicos de red
      if (!response.ok && response.status >= 500 && retries > 0) {
        await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 segundo
        return this.optimizedFetch(url, options, retries - 1);
      }
      
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      
      // Solo reintentar errores de red, no errores de abort
      if (retries > 0 && error instanceof Error && 
          (error.name === 'TypeError' || error.message.includes('network'))) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        return this.optimizedFetch(url, options, retries - 1);
      }
      
      throw error;
    }
  }

  getStats() {
    return {
      activeErrorKeys: this.errorCounts.size,
      recentHealthChecks: this.healthCheckTimes.length,
      config: this.config
    };
  }
}

export function usePerformanceOptimizer(config?: Partial<PerformanceConfig>) {
  const managerRef = useRef<PerformanceManager>();
  
  useEffect(() => {
    managerRef.current = PerformanceManager.getInstance(config ? { ...DEFAULT_CONFIG, ...config } : undefined);
    
    console.log('🚀 Performance optimizer initialized:', {
      timestamp: new Date().toISOString(),
      config: managerRef.current.getStats().config
    });
    
    // Reportar estadísticas cada 5 minutos en modo debug
    if (config?.debugMode) {
      const interval = setInterval(() => {
        console.log('📊 Performance stats:', managerRef.current?.getStats());
      }, 5 * 60 * 1000);
      
      return () => clearInterval(interval);
    }
  }, []);

  const throttledLog = useCallback((
    level: 'log' | 'warn' | 'error', 
    message: string, 
    data?: any
  ) => {
    managerRef.current?.throttledLog(level, message, data);
  }, []);

  const shouldLogError = useCallback((errorKey: string) => {
    return managerRef.current?.shouldLogError(errorKey) ?? true;
  }, []);

  const shouldPerformHealthCheck = useCallback(() => {
    return managerRef.current?.shouldPerformHealthCheck() ?? true;
  }, []);

  const optimizedFetch = useCallback((
    url: string, 
    options: RequestInit = {}, 
    retries: number = 2
  ) => {
    return managerRef.current?.optimizedFetch(url, options, retries) ?? fetch(url, options);
  }, []);

  const getStats = useCallback(() => {
    return managerRef.current?.getStats() ?? { 
      activeErrorKeys: 0, 
      recentHealthChecks: 0, 
      config: DEFAULT_CONFIG 
    };
  }, []);

  return {
    throttledLog,
    shouldLogError,
    shouldPerformHealthCheck,
    optimizedFetch,
    getStats
  };
}

// HOC para componentes que necesiten optimización automática
export function withPerformanceOptimization<T extends object>(
  Component: React.ComponentType<T>,
  options?: {
    enableErrorThrottling?: boolean;
    enableHealthCheckThrottling?: boolean;
  }
) {
  return function PerformanceOptimizedComponent(props: T) {
    const { throttledLog } = usePerformanceOptimizer({
      debugMode: process.env.NODE_ENV === 'development'
    });
    
    useEffect(() => {
      // Override console methods para throttling automático
      if (options?.enableErrorThrottling) {
        const originalError = console.error;
        const originalWarn = console.warn;
        
        console.error = (...args) => throttledLog('error', args.join(' '), args);
        console.warn = (...args) => throttledLog('warn', args.join(' '), args);
        
        return () => {
          console.error = originalError;
          console.warn = originalWarn;
        };
      }
    }, [throttledLog]);
    
    return <Component {...props} />;
  };
}